import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { adminResendConfirmation, listResendAttempts } from "@/lib/admin-resend.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MailWarning, Send } from "lucide-react";

export const Route = createFileRoute("/admin/resend-pokusy")({ component: Page });

const fmt = (s: string) => new Date(s).toLocaleString("cs-CZ");

function Page() {
  const fetchFn = useServerFn(listResendAttempts);
  const resendFn = useServerFn(adminResendConfirmation);
  const qc = useQueryClient();
  const [email, setEmail] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "resend-attempts"],
    queryFn: () => fetchFn(),
  });

  const resend = useMutation({
    mutationFn: (targetEmail: string) =>
      resendFn({
        data: {
          email: targetEmail,
          redirectTo: `${window.location.origin}/auth`,
        },
      }),
    onSuccess: (res, targetEmail) => {
      const label =
        res.status === "sent" ? "odeslán" : res.status === "skipped" ? "přeskočen (účet potvrzen / neexistuje)" : "selhal";
      toast.success(`Potvrzovací e-mail pro ${targetEmail}: ${label}`);
      qc.invalidateQueries({ queryKey: ["admin", "resend-attempts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleManualSend = (e: React.FormEvent) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!v) return;
    resend.mutate(v);
    setEmail("");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <MailWarning className="h-5 w-5" />
        <h1 className="text-2xl font-semibold">Pokusy o znovuzaslání potvrzovacího e-mailu</h1>
      </div>
      <p className="text-sm text-muted-foreground">
        Limit pro uživatele: 3 pokusy / hod a min. 60 s mezi pokusy. Admin manuální odeslání limit nepodléhá a zapisuje se do auditu.
      </p>

      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ruční odeslání</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleManualSend} className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Label htmlFor="admin-resend-email">E-mail uživatele</Label>
              <Input
                id="admin-resend-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="uzivatel@example.com"
                required
              />
            </div>
            <Button type="submit" disabled={resend.isPending} className="gap-2">
              <Send className="h-4 w-4" />
              {resend.isPending ? "Odesílám…" : "Znovu odeslat"}
            </Button>
          </form>
          <p className="mt-2 text-xs text-muted-foreground">
            Pro ochranu soukromí systém nepotvrzuje, zda účet existuje. Detail je v auditu níže.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Podle uživatele ({data?.summary.length ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Načítání…</p>
          ) : !data?.summary.length ? (
            <p className="text-sm text-muted-foreground">Žádné záznamy.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-mail</TableHead>
                  <TableHead className="text-right">Posl. hodina</TableHead>
                  <TableHead className="text-right">24 h</TableHead>
                  <TableHead className="text-right">Celkem (30 d)</TableHead>
                  <TableHead className="text-right">Poslední pokus</TableHead>
                  <TableHead className="text-right">Akce</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.summary.map((r) => (
                  <TableRow key={r.email}>
                    <TableCell className="font-mono text-xs">{r.email}</TableCell>
                    <TableCell className="text-right">
                      {r.lastHour >= 3 ? (
                        <Badge className="bg-amber-500 hover:bg-amber-500">{r.lastHour}/3</Badge>
                      ) : (
                        <span>{r.lastHour}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{r.last24h}</TableCell>
                    <TableCell className="text-right">{r.total}</TableCell>
                    <TableCell className="text-right">{fmt(r.lastAt)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resend.mutate(r.email)}
                        disabled={resend.isPending}
                      >
                        Odeslat
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Audit ručního odeslání ({data?.audit.length ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Načítání…</p>
          ) : !data?.audit.length ? (
            <p className="text-sm text-muted-foreground">Žádné záznamy.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Čas</TableHead>
                  <TableHead>Cíl. e-mail</TableHead>
                  <TableHead>Stav</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.audit.map((row) => {
                  const details = (row.details ?? {}) as { status?: string; error?: string | null };
                  const status = details.status ?? "?";
                  const variant =
                    status === "sent" ? "default" : status === "skipped" ? "secondary" : "destructive";
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="text-xs">{fmt(row.created_at)}</TableCell>
                      <TableCell className="font-mono text-xs">{row.target_email}</TableCell>
                      <TableCell>
                        <Badge variant={variant}>{status}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{row.admin_user_id?.slice(0, 8)}…</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{details.error ?? "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historie pokusů (posledních 200)</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Načítání…</p>
          ) : !data?.recent.length ? (
            <p className="text-sm text-muted-foreground">Žádné záznamy.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-mail</TableHead>
                  <TableHead className="text-right">Čas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent.map((r, i) => (
                  <TableRow key={`${r.email}-${r.attempted_at}-${i}`}>
                    <TableCell className="font-mono text-xs">{r.email}</TableCell>
                    <TableCell className="text-right">{fmt(r.attempted_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
